import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { Flame, Upload, FileSpreadsheet, ClipboardPaste, X, CheckCircle2, AlertCircle, Loader2, Image as ImageIcon, ScanLine, MoreVertical, User, Phone as PhoneIcon, MapPin, Calendar, ShoppingCart, IndianRupee } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../lib/auth';
import { bcIcon } from '../lib/businessCategories';

// Ticket 281089 (Stage E, item 5): AI must not read contact screenshots. The
// Screenshot tab is hidden, so sellers add contacts with the existing Paste / File
// tabs (manual entry). The server blocks the screenshot endpoints as well.
const SCREENSHOT_IMPORT_ENABLED = false;

// Feb 2026 — order-status pill colours; matches the same palette used on
// the Orders and Dashboard pages so buyers/sellers see consistent labels.
const CUSTOMER_STATUS_COLORS = {
  DELIVERED: 'bg-emerald-100 text-emerald-700',
  SHIPPED: 'bg-blue-100 text-blue-700',
  PACKED: 'bg-fab-primary-lighter text-fab-primary',
  CONFIRMED: 'bg-fab-primary-lighter text-fab-primary',
  PROCESSING: 'bg-amber-100 text-amber-700',
  PENDING_PAYMENT: 'bg-neutral-100 text-neutral-700',
  AWAITING_VERIFICATION: 'bg-orange-100 text-orange-700',
  CANCELLED: 'bg-red-100 text-red-700',
};

export default function Customers() {
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState('');
  const [importOpen, setImportOpen] = useState(false);
  const [presetGroup, setPresetGroup] = useState(null); // {id,name} when opened via ⋮ menu from a broadcast group
  const [searchParams, setSearchParams] = useSearchParams();
  const [profileId, setProfileId] = useState(null);

  const load = () => api.get('/customers').then(({ data }) => setItems(data.items || []));
  useEffect(() => { load(); }, []);

  // Deep-link support: /dashboard/customers?import=1&group_id=…&group_name=…
  // Opens the Import modal pre-configured to add members to the given
  // broadcast group. Used by the WhatsApp Groups ⋮ menu.
  useEffect(() => {
    if (searchParams.get('import') === '1') {
      const gid = searchParams.get('group_id') || '';
      const gname = searchParams.get('group_name') || '';
      if (gid) setPresetGroup({ id: gid, name: gname });
      setImportOpen(true);
      // Clear query params so a page refresh doesn't re-open the modal.
      const clean = new URLSearchParams(searchParams);
      clean.delete('import'); clean.delete('group_id'); clean.delete('group_name');
      setSearchParams(clean, { replace: true });
    }
  }, []);

  const fmt = (n) => '₹' + Number(n || 0).toLocaleString('en-IN');
  const filtered = useMemo(() => {
    if (!query.trim()) return items;
    const q = query.toLowerCase();
    return items.filter(c =>
      (c.name || '').toLowerCase().includes(q) ||
      (c.phone || '').toLowerCase().includes(q) ||
      (c.city || '').toLowerCase().includes(q)
    );
  }, [items, query]);

  return (
    <div className="space-y-4 fade-up" data-testid="customers-page">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <input
            data-testid="customers-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, phone, city..."
            className="w-full px-3 py-2 rounded-lg border border-fab-primary-lighter text-[13px] focus:outline-none focus:border-fab-primary"
          />
        </div>
        <div className="text-[12px] text-fab-text-muted whitespace-nowrap">
          <span className="font-semibold text-fab-text">{items.length}</span> total
        </div>
        <button
          data-testid="customers-import-btn"
          onClick={() => setImportOpen(true)}
          className="btn-primary px-4 py-2 rounded-lg text-[13px] font-semibold flex items-center gap-2"
        >
          <Upload className="w-4 h-4" /> Import customers
        </button>
      </div>

      <div className="fab-card overflow-hidden">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="text-[10px] text-fab-text-muted uppercase tracking-wider bg-fab-elevated">
              <th className="text-left px-4 py-3 font-semibold">Customer</th>
              <th className="text-left px-4 py-3 font-semibold">Phone</th>
              <th className="text-left px-4 py-3 font-semibold">City</th>
              <th className="text-left px-4 py-3 font-semibold">Orders</th>
              <th className="text-left px-4 py-3 font-semibold">Spent</th>
              <th className="text-left px-4 py-3 font-semibold">Tags</th>
              <th className="w-10 px-2 py-3" aria-label="Actions"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <tr key={c.id} className="border-b border-fab-primary-lighter last:border-0 hover:bg-fab-elevated" data-testid={`customer-row-${c.id}`}>
                <td className="px-4 py-3 font-semibold text-fab-text">{c.name || '—'}</td>
                <td className="px-4 py-3 font-mono text-fab-text-secondary">{c.phone}</td>
                <td className="px-4 py-3 text-fab-text-secondary">{c.city || '—'}</td>
                <td className="px-4 py-3 text-fab-text">{c.total_orders}</td>
                <td className="px-4 py-3 font-semibold text-fab-text">{fmt(c.total_spent)}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-1 flex-wrap">
                    {(c.tags || []).map((t, i) => (
                      <span key={i} className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${t === 'hot_lead' ? 'bg-orange-100 text-fab-orange' : t === 'vip' ? 'bg-fab-primary-lighter text-fab-primary' : t === 'imported' ? 'bg-emerald-100 text-emerald-700' : 'bg-fab-elevated text-fab-text-secondary'}`}>
                        {t === 'hot_lead' ? <span className="flex items-center gap-0.5"><Flame className="w-3 h-3" /> Hot</span> : t}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="px-2 py-3 text-right">
                  <button
                    type="button"
                    onClick={() => setProfileId(c.id)}
                    className="p-1.5 rounded-full text-fab-text-muted hover:text-fab-primary hover:bg-fab-primary-lighter"
                    aria-label={`Open profile for ${c.name || c.phone}`}
                    data-testid={`customer-actions-${c.id}`}
                  ><MoreVertical className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan="7" className="text-center text-fab-text-muted py-10">
                {items.length === 0 ? 'No customers yet. Click “Import customers” to bring in your existing buyer list.' : 'No customers match your search.'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {importOpen && <ImportModal presetGroup={presetGroup} onClose={() => { setImportOpen(false); setPresetGroup(null); }} onDone={() => { setImportOpen(false); setPresetGroup(null); load(); }} />}
      {profileId && <CustomerProfileDrawer customerId={profileId} onClose={() => setProfileId(null)} />}
    </div>
  );
}

function ImportModal({ onClose, onDone, presetGroup = null }) {
  const { seller } = useAuth();
  const [mode, setMode] = useState('file'); // 'file' | 'paste' | 'screenshot'
  const [file, setFile] = useState(null);
  const [screenshots, setScreenshots] = useState([]); // File[]
  const [shotProgress, setShotProgress] = useState({ done: 0, total: 0, current: '', errors: [] });
  const [mergedContacts, setMergedContacts] = useState(null); // {contacts:[], stats:{...}} after OCR-merge phase
  const [pasted, setPasted] = useState('');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Group + interested-category assignment
  const [groups, setGroups] = useState([]);
  const [categories, setCategories] = useState([]);
  // When a presetGroup is provided (deep-link from a broadcast group's ⋮ menu),
  // we lock the picker to that group and hide the mode toggle.
  const [groupMode, setGroupMode] = useState(presetGroup ? 'existing' : 'new');
  const [groupId, setGroupId] = useState(presetGroup?.id || '');
  const [groupName, setGroupName] = useState('');
  const [catIds, setCatIds] = useState([]);

  useEffect(() => {
    api.get('/whatsapp/groups').then(({ data }) => setGroups(data?.items || [])).catch(() => {});
    // Feb 2026: import-modal category picker shows the seller's Business
    // Categories directly (from auth context) — never expanded into
    // master-category sub-types. `categories` now holds an array of BC name
    // strings, e.g. ["Fabric","Ladies Wear",...].
    setCategories(seller?.business_categories || []);
  }, [seller]);

  const endpoint = '/customers/import';
  const ACCEPTED_IMG_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
  const MAX_SHOT_BYTES = 8 * 1024 * 1024;

  const addScreenshots = (newFiles) => {
    const arr = Array.from(newFiles || []);
    const accepted = [];
    const rejected = [];
    for (const f of arr) {
      const t = (f.type || '').toLowerCase();
      if (!ACCEPTED_IMG_TYPES.includes(t)) { rejected.push(`${f.name} (not an image)`); continue; }
      if (f.size > MAX_SHOT_BYTES) { rejected.push(`${f.name} (over 8 MB)`); continue; }
      accepted.push(f);
    }
    if (rejected.length) toast.message(`Skipped ${rejected.length}: ${rejected.slice(0, 3).join(', ')}${rejected.length > 3 ? '…' : ''}`);
    // Dedupe by name+size so the same file isn't added twice
    setScreenshots((prev) => {
      const seen = new Set(prev.map((f) => `${f.name}|${f.size}`));
      const out = [...prev];
      for (const f of accepted) {
        const key = `${f.name}|${f.size}`;
        if (!seen.has(key)) { seen.add(key); out.push(f); }
      }
      return out;
    });
    setPreview(null); setMergedContacts(null); setError('');
  };

  const removeScreenshot = (idx) => {
    setScreenshots((prev) => prev.filter((_, i) => i !== idx));
    setPreview(null); setMergedContacts(null);
  };

  const buildForm = (dryRun) => {
    const fd = new FormData();
    if (mode === 'file' && file) fd.append('file', file);
    if (mode === 'paste') fd.append('pasted_text', pasted);
    if (mode === 'screenshot' && mergedContacts) {
      // Submit merged contacts as paste-style text so the existing import
      // endpoint handles group/category assignment uniformly.
      const lines = mergedContacts.contacts.map((c) =>
        c.name ? `${c.name}, ${c.phone}` : c.phone
      ).join('\n');
      fd.append('pasted_text', lines);
    }
    if (dryRun) fd.append('dry_run', 'true');
    if (groupMode === 'existing' && groupId) fd.append('group_id', groupId);
    else if (groupMode === 'new' && groupName.trim()) fd.append('group_name', groupName.trim());
    if (catIds.length > 0) fd.append('category_ids', catIds.join(','));
    return fd;
  };

  const toggleCat = (cid) => setCatIds((arr) => arr.includes(cid) ? arr.filter(x => x !== cid) : [...arr, cid]);

  /** Phase 1 for screenshot mode: OCR each image, merge + dedupe client-side. */
  const processScreenshots = async () => {
    if (screenshots.length === 0) { setError('Add at least one screenshot first.'); return; }
    setError('');
    setShotProgress({ done: 0, total: screenshots.length, current: '', errors: [] });
    setLoading(true);
    const byPhone = new Map(); // phone → name
    let dupsAcrossImages = 0;
    let skippedInvalid = 0;
    const skippedNoPhone = [];
    const imageStats = [];
    const errors = [];

    try {
      for (let i = 0; i < screenshots.length; i++) {
        const f = screenshots[i];
        setShotProgress({ done: i, total: screenshots.length, current: f.name, errors });
        const fd = new FormData();
        fd.append('file', f);
        try {
          const { data } = await api.post('/customers/import/screenshot-ocr', fd);
          imageStats.push({ filename: f.name, contacts: (data.contacts || []).length });
          for (const c of (data.contacts || [])) {
            const ph = c.phone;
            if (!ph) continue;
            if (byPhone.has(ph)) {
              dupsAcrossImages += 1;
              const existing = byPhone.get(ph);
              if (c.name && !existing) byPhone.set(ph, c.name);
            } else {
              byPhone.set(ph, c.name || '');
            }
          }
          skippedInvalid += (data.skipped_invalid || 0);
          for (const n of (data.skipped_no_phone || [])) skippedNoPhone.push(n);
        } catch (e) {
          errors.push(`${f.name}: ${e.response?.data?.detail || e.message || 'OCR failed'}`);
          imageStats.push({ filename: f.name, error: true });
        }
        // Yield to the browser for UI repaint
        await new Promise((r) => setTimeout(r, 0));
      }
      setShotProgress({ done: screenshots.length, total: screenshots.length, current: '', errors });

      const contacts = Array.from(byPhone.entries()).map(([phone, name]) => ({ phone, name }));
      setMergedContacts({
        contacts,
        stats: {
          images_processed: screenshots.length,
          images_failed: errors.length,
          contacts_detected: contacts.length + dupsAcrossImages,
          duplicates_removed: dupsAcrossImages,
          unique_phones: contacts.length,
          skipped_invalid: skippedInvalid,
          skipped_no_phone: skippedNoPhone.slice(0, 50),
          skipped_no_phone_count: skippedNoPhone.length,
          image_stats: imageStats,
          errors,
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const doPreview = async () => {
    setError('');
    if (mode === 'file' && !file) { setError('Choose a CSV or Excel file first.'); return; }
    if (mode === 'paste' && !pasted.trim()) { setError('Paste at least one contact line.'); return; }
    if (mode === 'screenshot') {
      if (!mergedContacts) { await processScreenshots(); return; }
      if (mergedContacts.contacts.length === 0) { setError('No valid contacts extracted from these screenshots.'); return; }
    }
    // Feb 2026: Skip option removed — every import must land in a broadcast
    // group so newly-imported customers are never left unassigned.
    if (groupMode === 'new' && !groupName.trim()) {
      setError('Please name the new broadcast group before importing.'); return;
    }
    if (groupMode === 'existing' && !groupId) {
      setError('Please choose which existing broadcast group these customers belong to.'); return;
    }
    setLoading(true);
    try {
      const { data } = await api.post(endpoint, buildForm(true));
      // For screenshot mode, enrich the dry-run response with the OCR-stage stats
      if (mode === 'screenshot' && mergedContacts) {
        data.images_processed = mergedContacts.stats.images_processed;
        data.images_failed = mergedContacts.stats.images_failed;
        data.contacts_detected = mergedContacts.stats.contacts_detected;
        data.duplicates_removed = mergedContacts.stats.duplicates_removed;
        data.skipped_no_phone = mergedContacts.stats.skipped_no_phone;
        data.skipped_no_phone_count = mergedContacts.stats.skipped_no_phone_count;
        data.engine = 'claude-sonnet-4-5';
      }
      setPreview(data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Preview failed.');
    } finally {
      setLoading(false);
    }
  };

  const doImport = async () => {
    if (!preview) { await doPreview(); return; }
    setSubmitting(true); setError('');
    try {
      const { data } = await api.post(endpoint, buildForm(false));
      toast.success(`Imported ${data.created} new · updated ${data.updated} existing`);
      onDone();
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Import failed.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" data-testid="import-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[calc(100vh_-_2rem)] overflow-hidden flex flex-col">
        <div className="px-5 py-4 border-b border-fab-primary-lighter flex items-center justify-between">
          <div>
            <div className="font-display font-bold text-fab-text text-[16px]">
              {presetGroup ? `Add members to ${presetGroup.name || 'group'}` : 'Import customers'}
            </div>
            <div className="text-[12px] text-fab-text-muted">
              {presetGroup ? (SCREENSHOT_IMPORT_ENABLED ? 'Paste numbers, upload a file, or scan screenshots — everyone will join this group.' : 'Paste numbers or upload a file — everyone will join this group.') : 'Upload a CSV or Excel file, or paste names + phone numbers.'}
            </div>
          </div>
          <button data-testid="import-close" onClick={onClose} className="p-1.5 hover:bg-fab-elevated rounded-lg"><X className="w-5 h-5" /></button>
        </div>

        <div className="px-5 pt-4">
          <div className="inline-flex p-1 rounded-lg bg-fab-elevated">
            <button
              data-testid="import-tab-file"
              onClick={() => { setMode('file'); setPreview(null); setError(''); }}
              className={`px-3 py-1.5 rounded-md text-[12px] font-semibold flex items-center gap-2 ${mode === 'file' ? 'bg-white shadow-sm text-fab-text' : 'text-fab-text-secondary'}`}
            >
              <FileSpreadsheet className="w-4 h-4" /> CSV / Excel
            </button>
            <button
              data-testid="import-tab-paste"
              onClick={() => { setMode('paste'); setPreview(null); setError(''); }}
              className={`px-3 py-1.5 rounded-md text-[12px] font-semibold flex items-center gap-2 ${mode === 'paste' ? 'bg-white shadow-sm text-fab-text' : 'text-fab-text-secondary'}`}
            >
              <ClipboardPaste className="w-4 h-4" /> Paste text
            </button>
            {SCREENSHOT_IMPORT_ENABLED && (
              <button
                data-testid="import-tab-screenshot"
                onClick={() => { setMode('screenshot'); setPreview(null); setError(''); }}
                className={`px-3 py-1.5 rounded-md text-[12px] font-semibold flex items-center gap-2 ${mode === 'screenshot' ? 'bg-white shadow-sm text-fab-text' : 'text-fab-text-secondary'}`}
              >
                <ScanLine className="w-4 h-4" /> Screenshot
              </button>
            )}
          </div>
          {mode === 'screenshot' && (
            <div className="mt-2 text-[11px] text-fab-text-muted flex items-center gap-1.5">
              <ImageIcon className="w-3 h-3" /> Powered by Claude Sonnet 4.5 vision — reads names + numbers from any WhatsApp / contacts screenshot.
            </div>
          )}
        </div>

        <div className="px-5 py-4 flex-1 overflow-y-auto">
          {mode === 'file' ? (
            <div>
              <label className="block">
                <div className="border-2 border-dashed border-fab-primary-lighter rounded-xl p-6 text-center hover:border-fab-primary transition-colors cursor-pointer">
                  <Upload className="w-8 h-8 mx-auto text-fab-text-muted mb-2" />
                  <div className="text-[13px] font-semibold text-fab-text">
                    {file ? file.name : 'Choose a CSV or .xlsx file'}
                  </div>
                  <div className="text-[11px] text-fab-text-muted mt-1">
                    Up to 5 MB. Columns auto-detected — phone first or name first both work.
                  </div>
                  <input
                    data-testid="import-file-input"
                    type="file"
                    accept=".csv,.xlsx,.xlsm,.txt"
                    onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); setError(''); }}
                    className="hidden"
                  />
                </div>
              </label>
            </div>
          ) : mode === 'screenshot' ? (
            <div data-testid="import-screenshot-multi">
              <div
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onDrop={(e) => {
                  e.preventDefault(); e.stopPropagation();
                  if (e.dataTransfer?.files?.length) addScreenshots(e.dataTransfer.files);
                }}
                className="border-2 border-dashed border-fab-primary-lighter rounded-xl p-5 text-center hover:border-fab-primary transition-colors"
              >
                <label className="block cursor-pointer">
                  <ScanLine className="w-8 h-8 mx-auto text-fab-text-muted mb-2" />
                  <div className="text-[13px] font-semibold text-fab-text">
                    {screenshots.length === 0
                      ? 'Drop 1–50 screenshots here, or click to choose'
                      : `${screenshots.length} screenshot${screenshots.length === 1 ? '' : 's'} ready · click to add more`}
                  </div>
                  <div className="text-[11px] text-fab-text-muted mt-1">
                    JPEG / PNG / WEBP · 8 MB each · WhatsApp group lists, contact screenshots, broadcast member screenshots all work.
                  </div>
                  <input
                    data-testid="import-screenshot-input"
                    type="file"
                    multiple
                    accept="image/jpeg,image/png,image/webp"
                    onChange={(e) => addScreenshots(e.target.files)}
                    className="hidden"
                  />
                </label>
              </div>

              {screenshots.length > 0 && (
                <div className="mt-3" data-testid="import-shot-thumbs">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[11px] uppercase tracking-wider text-fab-text-muted font-semibold">{screenshots.length} screenshot{screenshots.length === 1 ? '' : 's'} queued</div>
                    <button
                      type="button"
                      data-testid="import-shots-clear"
                      onClick={() => { setScreenshots([]); setMergedContacts(null); setPreview(null); }}
                      className="text-[11px] font-semibold text-fab-text-secondary hover:text-fab-primary"
                    >Clear all</button>
                  </div>
                  <div className="grid grid-cols-5 sm:grid-cols-6 md:grid-cols-8 gap-2 max-h-44 overflow-y-auto">
                    {screenshots.map((f, i) => (
                      <div key={`${f.name}-${f.size}-${i}`} className="relative group">
                        <img alt={f.name} src={URL.createObjectURL(f)} className="w-full aspect-square object-cover rounded border border-fab-primary-lighter" />
                        <button
                          type="button"
                          data-testid={`import-shot-remove-${i}`}
                          onClick={() => removeScreenshot(i)}
                          className="absolute -top-1.5 -right-1.5 bg-white border border-fab-primary-lighter rounded-full p-0.5 opacity-0 group-hover:opacity-100 hover:bg-red-50 hover:border-red-300 transition-opacity"
                          title="Remove"
                        >
                          <X className="w-3 h-3 text-red-600" />
                        </button>
                        <div className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-[9px] px-1 py-0.5 truncate font-mono">{f.name}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {loading && shotProgress.total > 0 && (
                <div className="mt-3 border border-fab-primary-lighter rounded-lg p-3 bg-fab-elevated/40" data-testid="import-shot-progress">
                  <div className="flex items-center justify-between text-[12px] font-semibold text-fab-text mb-1.5">
                    <span>Reading screenshots with AI vision…</span>
                    <span data-testid="import-shot-progress-count">{shotProgress.done} / {shotProgress.total}</span>
                  </div>
                  <div className="w-full h-2 bg-white rounded-full overflow-hidden">
                    <div
                      className="h-full bg-fab-primary transition-all"
                      style={{ width: `${shotProgress.total ? Math.round((shotProgress.done / shotProgress.total) * 100) : 0}%` }}
                    />
                  </div>
                  {shotProgress.current && (
                    <div className="mt-1.5 text-[11px] text-fab-text-muted font-mono truncate">Processing image {shotProgress.done + 1} of {shotProgress.total}: {shotProgress.current}</div>
                  )}
                </div>
              )}

              {mergedContacts && !loading && (
                <div className="mt-3 border border-emerald-200 rounded-lg p-3 bg-emerald-50/50 text-[12px]" data-testid="import-shot-summary">
                  <div className="font-semibold text-emerald-900 mb-1">OCR complete</div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-emerald-900">
                    <div>Screenshots processed: <span className="font-mono font-semibold">{mergedContacts.stats.images_processed}</span></div>
                    <div>Contacts detected: <span className="font-mono font-semibold">{mergedContacts.stats.contacts_detected}</span></div>
                    <div>Duplicates removed: <span className="font-mono font-semibold">{mergedContacts.stats.duplicates_removed}</span></div>
                    <div>Ready to import: <span className="font-mono font-semibold">{mergedContacts.stats.unique_phones}</span></div>
                    {mergedContacts.stats.skipped_invalid > 0 && <div>Skipped (invalid): <span className="font-mono font-semibold">{mergedContacts.stats.skipped_invalid}</span></div>}
                    {mergedContacts.stats.images_failed > 0 && <div className="text-red-700">Failed screenshots: <span className="font-mono font-semibold">{mergedContacts.stats.images_failed}</span></div>}
                  </div>
                  {mergedContacts.stats.errors?.length > 0 && (
                    <div className="mt-2 text-[11px] text-red-700">
                      {mergedContacts.stats.errors.slice(0, 3).map((e, i) => (<div key={i}>• {e}</div>))}
                      {mergedContacts.stats.errors.length > 3 && <div>… +{mergedContacts.stats.errors.length - 3} more errors</div>}
                    </div>
                  )}
                </div>
              )}

              <div className="mt-2 text-[11px] text-fab-text-muted flex items-center gap-1.5">
                <ImageIcon className="w-3 h-3" /> Powered by Claude Sonnet 4.5 vision — works on WhatsApp group lists, broadcast member screenshots, and phone contact screenshots.
              </div>
            </div>
          ) : (
            <div>
              <textarea
                data-testid="import-paste-input"
                value={pasted}
                onChange={(e) => { setPasted(e.target.value); setPreview(null); setError(''); }}
                placeholder={`Ramesh Jain, 9876543210\nPriya Sharma, +91 87654 32109\nSuresh Kumar - 8888888888\n9123456789`}
                rows={8}
                className="w-full px-3 py-2 rounded-lg border border-fab-primary-lighter text-[13px] font-mono focus:outline-none focus:border-fab-primary"
              />
              <div className="text-[11px] text-fab-text-muted mt-1">
                One contact per line. Use comma, tab, or dash to separate name from number. Lines without a phone are skipped.
              </div>
            </div>
          )}

          {/* Broadcast group + interested categories */}
          <div className="mt-4 border border-fab-primary-lighter rounded-lg p-3 bg-fab-elevated/40 space-y-3" data-testid="import-group-section">
            {presetGroup ? (
              <div>
                <div className="text-[11px] uppercase tracking-wider text-fab-text-muted font-semibold">Adding members to</div>
                <div className="mt-1 flex items-center gap-2" data-testid="import-preset-group">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-fab-primary text-white text-[12px] font-semibold">
                    <CheckCircle2 className="w-3.5 h-3.5" /> {presetGroup.name || 'Selected group'}
                  </span>
                  <span className="text-[11px] text-fab-text-muted">Locked · new customers will join this group only.</span>
                </div>
              </div>
            ) : (
              <>
                <div className="text-[11px] uppercase tracking-wider text-fab-text-muted font-semibold">Assign to broadcast group</div>
                <div className="flex flex-wrap gap-2 text-[12px]">
                  {[['new', 'New group'], ['existing', 'Existing group']].map(([v, l]) => (
                    <button
                      key={v}
                      type="button"
                      data-testid={`import-group-mode-${v}`}
                      onClick={() => { setGroupMode(v); setPreview(null); }}
                      className={`px-3 py-1 rounded-full border text-[12px] font-semibold ${groupMode === v ? 'bg-fab-primary text-white border-fab-primary' : 'bg-white text-fab-text-secondary border-fab-primary-lighter'}`}
                    >{l}</button>
                  ))}
                </div>
                {groupMode === 'new' && (
                  <input
                    data-testid="import-group-name-input"
                    value={groupName}
                    onChange={(e) => { setGroupName(e.target.value); setPreview(null); }}
                    placeholder="e.g. Surat Resellers, Mumbai VIPs, Cold Leads"
                    className="w-full px-3 py-2 rounded-lg border border-fab-primary-lighter text-[13px] focus:outline-none focus:border-fab-primary"
                  />
                )}
                {groupMode === 'existing' && (
                  <select
                    data-testid="import-group-existing-select"
                    value={groupId}
                    onChange={(e) => { setGroupId(e.target.value); setPreview(null); }}
                    className="w-full px-3 py-2 rounded-lg border border-fab-primary-lighter text-[13px] focus:outline-none focus:border-fab-primary"
                  >
                    <option value="">— Choose a group —</option>
                    {groups.map((g) => (
                      <option key={g.id} value={g.id}>{g.group_name} ({g.member_count || (g.members || []).length})</option>
                    ))}
                  </select>
                )}
              </>
            )}
            {categories.length > 0 && (
              <div>
                <div className="text-[11px] text-fab-text-muted mb-1.5">Interested categories — buyers will hear about new arrivals here</div>
                <div className="flex flex-wrap gap-1.5" data-testid="import-cat-picker">
                  {categories.map((c) => {
                    const slug = (c || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
                    return (
                      <button
                        key={c}
                        type="button"
                        data-testid={`import-cat-${slug}`}
                        onClick={() => { toggleCat(c); setPreview(null); }}
                        className={`px-2.5 py-1 rounded-full border text-[12px] font-semibold transition-colors ${catIds.includes(c) ? 'bg-fab-primary text-white border-fab-primary' : 'bg-white text-fab-text-secondary border-fab-primary-lighter hover:border-fab-primary'}`}
                      >
                        <span className="mr-1">{bcIcon(c)}</span>{c}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {error && (
            <div className="mt-3 flex items-start gap-2 text-[12px] text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> <div>{error}</div>
            </div>
          )}

          {preview && (
            <div className="mt-4 border border-fab-primary-lighter rounded-lg overflow-hidden" data-testid="import-preview">
              <div className="px-4 py-2 bg-fab-elevated text-[11px] uppercase tracking-wider text-fab-text-muted font-semibold flex items-center justify-between flex-wrap gap-2">
                <span>Preview {preview.engine ? <span className="ml-2 normal-case tracking-normal text-fab-primary">via {preview.engine}</span> : null}</span>
                <span className="text-fab-text font-mono normal-case tracking-normal">
                  {typeof preview.images_processed === 'number' && `${preview.images_processed} screenshots · `}
                  {typeof preview.contacts_detected === 'number' && `${preview.contacts_detected} detected · `}
                  {typeof preview.duplicates_removed === 'number' && `${preview.duplicates_removed} dupes removed · `}
                  {preview.unique_phones} ready
                  {typeof preview.skipped_invalid === 'number' && preview.skipped_invalid > 0 && ` · ${preview.skipped_invalid} invalid`}
                  {typeof preview.skipped_name_only === 'number' && preview.skipped_name_only > 0 && ` · ${preview.skipped_name_only} name-only`}
                  {typeof preview.skipped_no_phone_count === 'number' && preview.skipped_no_phone_count > 0 && ` · ${preview.skipped_no_phone_count} missing numbers`}
                  {typeof preview.skipped_duplicate_in_payload === 'number' && preview.skipped_duplicate_in_payload > 0 && ` · ${preview.skipped_duplicate_in_payload} duplicates`}
                </span>
              </div>
              <div className="max-h-48 overflow-y-auto divide-y divide-fab-primary-lighter">
                {(preview.preview || []).map((c, i) => (
                  <div key={i} className="px-4 py-2 flex items-center justify-between text-[12px]">
                    <span className="font-semibold text-fab-text">{c.name || <span className="text-fab-text-muted italic">(no name)</span>}</span>
                    <span className="font-mono text-fab-text-secondary">{c.phone}</span>
                  </div>
                ))}
                {(preview.preview || []).length === 0 && (
                  <div className="px-4 py-3 text-center text-[12px] text-fab-text-muted">No valid contacts detected.</div>
                )}
              </div>
              {(preview.skipped_no_phone || []).length > 0 && (
                <div className="border-t border-fab-primary-lighter px-4 py-2 bg-amber-50" data-testid="import-missing-numbers">
                  <div className="text-[10px] uppercase tracking-wider text-amber-800 font-semibold mb-1">
                    Missing numbers · {preview.skipped_no_phone.length} skipped (no phone visible)
                  </div>
                  <div className="text-[11px] text-amber-900 font-mono leading-tight">
                    {preview.skipped_no_phone.slice(0, 10).join(' · ')}
                    {preview.skipped_no_phone.length > 10 ? ` · +${preview.skipped_no_phone.length - 10} more` : ''}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-fab-primary-lighter flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-[13px] font-semibold text-fab-text-secondary hover:bg-fab-elevated">Cancel</button>
          {!preview ? (
            <button
              data-testid="import-preview-btn"
              disabled={loading || (mode === 'screenshot' && screenshots.length === 0)}
              onClick={doPreview}
              className="btn-primary px-4 py-2 rounded-lg text-[13px] font-semibold flex items-center gap-2 disabled:opacity-50"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              {mode === 'screenshot' && !mergedContacts
                ? `Process ${screenshots.length || ''} screenshot${screenshots.length === 1 ? '' : 's'}`.trim()
                : 'Preview'}
            </button>
          ) : (
            <button
              data-testid="import-confirm-btn"
              disabled={submitting || preview.unique_phones === 0}
              onClick={doImport}
              className="btn-primary px-4 py-2 rounded-lg text-[13px] font-semibold flex items-center gap-2 disabled:opacity-50"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Import {preview.unique_phones} customer{preview.unique_phones === 1 ? '' : 's'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Feb 2026 — 3-dot Customer Profile drawer. Reuses the existing
// GET /api/customers/{cid} endpoint (already tenant-scoped: it filters by
// seller_id from the JWT, so a seller can never see another seller's
// customer). Shows every existing customer field + full order history.
function CustomerProfileDrawer({ customerId, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    let alive = true;
    api.get(`/customers/${customerId}`)
      .then(({ data }) => { if (alive) setData(data); })
      .catch((e) => { if (alive) setErr(e?.response?.data?.detail || 'Failed to load customer'); });
    return () => { alive = false; };
  }, [customerId]);

  // ESC to close (spec §9)
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const fmt = (n) => '₹' + Number(n || 0).toLocaleString('en-IN');
  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
  const orders = (data?.orders || []).slice().sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
  const lastOrderAt = orders[0]?.created_at || data?.last_order_at || data?.last_active_at;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex justify-end"
      onClick={onClose}
      data-testid="customer-profile-overlay"
    >
      <div
        className="bg-white w-full sm:max-w-lg h-full shadow-xl flex flex-col animate-slide-in-right"
        onClick={(e) => e.stopPropagation()}
        data-testid="customer-profile-drawer"
      >
        <div className="px-5 py-4 border-b border-fab-primary-lighter flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-wider text-fab-text-muted font-semibold">Customer Profile</div>
            <div className="font-display font-bold text-fab-text text-[18px] truncate" data-testid="customer-profile-name">
              {data ? (data.name || <span className="text-fab-text-muted italic">(no name)</span>) : '…'}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-fab-elevated rounded-lg shrink-0"
            aria-label="Close"
            data-testid="customer-profile-close"
          ><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {err && (
            <div className="text-[12.5px] bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5" /> {err}
            </div>
          )}
          {!data && !err && (
            <div className="text-center py-10 text-fab-text-muted flex items-center justify-center gap-2 text-[13px]">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          )}
          {data && (
            <>
              <section data-testid="customer-profile-basic">
                <div className="text-[10px] uppercase tracking-wider text-fab-text-muted font-semibold mb-2">Basic Information</div>
                <div className="space-y-1.5 text-[13px]">
                  <ProfileRow icon={User} label="Name" value={data.name || '—'} />
                  <ProfileRow icon={PhoneIcon} label="Phone" value={data.phone} mono />
                  {data.email && <ProfileRow icon={PhoneIcon} label="Email" value={data.email} />}
                  <ProfileRow icon={MapPin} label="City" value={data.city || '—'} />
                  {data.created_at && <ProfileRow icon={Calendar} label="Added" value={fmtDate(data.created_at)} />}
                </div>
              </section>

              <section data-testid="customer-profile-summary">
                <div className="text-[10px] uppercase tracking-wider text-fab-text-muted font-semibold mb-2">Customer Summary</div>
                <div className="grid grid-cols-3 gap-2">
                  <SummaryTile icon={ShoppingCart} label="Orders" value={data.total_orders || 0} />
                  <SummaryTile icon={IndianRupee} label="Total Spent" value={fmt(data.total_spent)} />
                  <SummaryTile icon={Calendar} label="Last Order" value={lastOrderAt ? fmtDate(lastOrderAt) : '—'} small />
                </div>
              </section>

              {(data.tags || []).length > 0 && (
                <section data-testid="customer-profile-tags">
                  <div className="text-[10px] uppercase tracking-wider text-fab-text-muted font-semibold mb-2">Tags</div>
                  <div className="flex gap-1 flex-wrap">
                    {(data.tags || []).map((t, i) => (
                      <span key={i} className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${t === 'hot_lead' ? 'bg-orange-100 text-fab-orange' : t === 'vip' ? 'bg-fab-primary-lighter text-fab-primary' : t === 'imported' ? 'bg-emerald-100 text-emerald-700' : 'bg-fab-elevated text-fab-text-secondary'}`}>
                        {t === 'hot_lead' ? <span className="inline-flex items-center gap-0.5"><Flame className="w-3 h-3" /> Hot</span> : t}
                      </span>
                    ))}
                  </div>
                </section>
              )}

              <section data-testid="customer-profile-orders">
                <div className="text-[10px] uppercase tracking-wider text-fab-text-muted font-semibold mb-2">Order History</div>
                {orders.length === 0 ? (
                  <div className="text-[12.5px] text-fab-text-muted bg-fab-elevated rounded-lg p-3">No orders yet.</div>
                ) : (
                  <div className="border border-fab-primary-lighter rounded-lg overflow-hidden">
                    <table className="w-full text-[12.5px]">
                      <thead>
                        <tr className="text-[10px] uppercase tracking-wider text-fab-text-muted bg-fab-elevated">
                          <th className="text-left px-3 py-2 font-semibold">Order ID</th>
                          <th className="text-left px-3 py-2 font-semibold">Date</th>
                          <th className="text-left px-3 py-2 font-semibold">Items</th>
                          <th className="text-left px-3 py-2 font-semibold">Amount</th>
                          <th className="text-left px-3 py-2 font-semibold">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orders.map((o) => (
                          <tr key={o.id} className="border-t border-fab-primary-lighter" data-testid={`customer-profile-order-${o.id}`}>
                            <td className="px-3 py-2 font-mono font-semibold text-fab-primary">
                              <a href={`/dashboard/orders/${o.id}`} className="hover:underline">{o.order_number || o.id.slice(0, 8)}</a>
                            </td>
                            <td className="px-3 py-2 text-fab-text-secondary">{fmtDate(o.created_at)}</td>
                            <td className="px-3 py-2 text-fab-text-secondary">{(o.items || []).length}</td>
                            <td className="px-3 py-2 font-semibold text-fab-text">{fmt(o.total_amount)}</td>
                            <td className="px-3 py-2">
                              <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${CUSTOMER_STATUS_COLORS[o.status] || 'bg-fab-elevated text-fab-text-secondary'}`}>
                                {(o.status || '').replace(/_/g, ' ')}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ProfileRow({ icon: Icon, label, value, mono }) {
  return (
    <div className="flex items-center gap-2 text-fab-text">
      <Icon className="w-3.5 h-3.5 text-fab-text-muted shrink-0" />
      <span className="text-fab-text-muted w-16">{label}</span>
      <span className={`${mono ? 'font-mono' : ''} font-semibold truncate`}>{value}</span>
    </div>
  );
}

function SummaryTile({ icon: Icon, label, value, small }) {
  return (
    <div className="bg-fab-elevated rounded-lg p-2.5">
      <div className="text-[10px] text-fab-text-muted uppercase tracking-wider flex items-center gap-1">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className={`font-display font-bold text-fab-text mt-0.5 ${small ? 'text-[13px]' : 'text-[16px]'}`}>{value}</div>
    </div>
  );
}
